import { useAppScopeStore } from '@frontend/scope';
import { SettingsSection } from '@frontend/settings-ui';
import { SkeletonLoader } from '@frontend/design-system';
import { useBusinessPreferencesData } from '../../api/business-preferences';
import {
  apiToFormValues,
  formValuesToApi,
  InsuranceFormValues,
} from '../../api/business-preferences/insurance/transform';
import { listField } from '../../utils/field-config-helpers';
import { InsuranceAccepted, OutOfNetworkInsurances } from '../business-preferences/insurance/fields';
import { Footer } from '../footer';
import { FormStateProvider } from '../form-state-context';
import { FormWrapper } from '../form-wrapper';
import { usePreferenceForm } from '../use-preference-form';

/**
 * Form config containing only the payer network fields
 * (insuranceAccepted & outOfNetworkInsurances).
 */
const payerNetworkFieldsConfig = {
  insuranceAccepted: listField(),
  outOfNetworkInsurances: listField(),
};

type PayerNetworkFieldsConfigTypes = typeof payerNetworkFieldsConfig;

/**
 * Transform that maps only the payer network fields to the API shape.
 */
const payerNetworkFormValuesToApi = (changedValues: Partial<InsuranceFormValues>) => {
  // Re-use the full insurance transform — it already handles partial payloads
  return formValuesToApi(changedValues);
};

const PayerNetworkConfigurationSkeleton = () => (
  <SettingsSection>
    <div css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SkeletonLoader height={20} width={130} />
      <SkeletonLoader height={16} width='55%' />
      <SkeletonLoader height={40} />
    </div>
    <div css={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
      <SkeletonLoader height={20} width={130} />
      <SkeletonLoader height={16} width='55%' />
      <SkeletonLoader height={160} />
    </div>
  </SettingsSection>
);

type PayerNetworkFormProps = {
  locationId: string;
  orgId: string;
  etag?: string;
  initialValues: InsuranceFormValues;
};

const PayerNetworkForm = ({ locationId, orgId, etag, initialValues }: PayerNetworkFormProps) => {
  const { submitLocation, isSaving } = usePreferenceForm<PayerNetworkFieldsConfigTypes>({
    locationId,
    orgId,
    etag,
    formValuesToApi: payerNetworkFormValuesToApi,
  });

  return (
    <SettingsSection>
      <FormWrapper<PayerNetworkFieldsConfigTypes>
        fields={payerNetworkFieldsConfig}
        locationId={locationId}
        initialValues={{
          insuranceAccepted: initialValues.insuranceAccepted,
          outOfNetworkInsurances: initialValues.outOfNetworkInsurances,
        }}
        onSubmit={submitLocation}
      >
        {({ getFieldProps, hasFormErrors }) => (
          <>
            <InsuranceAccepted {...getFieldProps('insuranceAccepted')} />
            <OutOfNetworkInsurances {...getFieldProps('outOfNetworkInsurances')} />
            <Footer isSaving={isSaving} locationId={locationId} hasFormErrors={hasFormErrors} />
          </>
        )}
      </FormWrapper>
    </SettingsSection>
  );
};

type PayerNetworkConfigurationFormProps = {
  locationId: string;
};

/**
 * Self-contained payer network configuration form.
 *
 * Fetches AI business-preferences data for the given location,
 * and renders the "Insurance Accepted" and "Out of Network Insurances" fields
 * with save/cancel support.
 */
export const PayerNetworkConfigurationForm = ({ locationId }: PayerNetworkConfigurationFormProps) => {
  const { selectedOrgId } = useAppScopeStore();

  const { dataByLocationId, etagByLocationId, isLoading } = useBusinessPreferencesData({
    selectedLocationIds: [locationId],
    selectedOrgId,
  });

  if (isLoading) {
    return <PayerNetworkConfigurationSkeleton />;
  }

  const initialValues = apiToFormValues(dataByLocationId[locationId]);

  return (
    <FormStateProvider>
      <PayerNetworkForm
        locationId={locationId}
        orgId={selectedOrgId}
        etag={etagByLocationId[locationId]}
        initialValues={initialValues}
        key={`${locationId}-${etagByLocationId[locationId]}`}
      />
    </FormStateProvider>
  );
};
